// @ts-check
/**
 * Známkovanie presne podľa metodiky ATF Online.
 *
 * Tabuľky boli prevzaté z aplikácie predlohy (Možnosti → Známkovanie →
 * `index.php?cat=opt&but=speedTab` a `...&but=errTab`), kde sa generujú
 * z cieľových hodnôt.
 *
 * Pravidlá:
 *  - hodnotí sa rýchlosť a chybovosť SAMOSTATNE a platí HORŠÍ stupeň,
 *  - nároky na rýchlosť rastú v desiatich obdobiach výučby (daných rozsahmi lekcií),
 *  - klasifikácia podľa chybovosti sa počas výučby nemení,
 *  - predvolený cieľ: 140 čistých úderov/min a 0,3 % chybovosti.
 */

export const GRADES = /** @type {const} */ ([
  { grade: 1, label: 'Výborný' },
  { grade: 2, label: 'Chválitebný' },
  { grade: 3, label: 'Dobrý' },
  { grade: 4, label: 'Dostatočný' },
  { grade: 5, label: 'Nedostatočný' },
]);

export const DEFAULT_TARGET_SPEED = 140;
export const DEFAULT_TARGET_ERROR_RATE = 0.3;

export const TARGET_SPEED_RANGE = { min: 50, max: 300 };
export const TARGET_ERROR_RATE_RANGE = { min: 0.1, max: 1.5 };

/**
 * Desiate období výučby podľa lekcií — presne ako v tabuľke predlohy.
 * @type {Array<[number, number]>}
 */
export const PERIOD_LESSON_RANGES = [
  [1, 8], [9, 15], [16, 22], [23, 29], [30, 36],
  [37, 43], [44, 49], [50, 57], [58, 62], [63, 66],
];

/**
 * Tabuľka rýchlosti pre predvolený cieľ 140 čistých úderov/min.
 * Riadok = stupeň, stĺpec = obdobie výučby (1–10).
 * Hodnota je najnižšia rýchlosť, ktorá ešte daný stupeň získa.
 * @type {Record<number, number[]>}
 */
export const SPEED_TABLE_FOR_140 = {
  1: [61, 69, 78, 87, 96, 105, 114, 122, 131, 140],
  2: [52, 60, 68, 77, 85, 94, 102, 110, 118, 127],
  3: [43, 50, 58, 66, 74, 83, 91, 98, 106, 114],
  4: [32, 39, 46, 54, 62, 69, 77, 84, 91, 99],
};

/**
 * Tabuľka chybovosti: hranice ako násobok cieľovej chybovosti.
 * Pre cieľ 0,3 % vychádzajú presne hodnoty z predlohy: 0,30 / 0,54 / 0,84 / 1,20.
 */
export const ERROR_RATE_MULTIPLIERS = [1, 1.8, 2.8, 4];

/**
 * Do ktorého z desiatich období patrí lekcia (1–66).
 * @param {number} lessonNumber
 */
export function periodForLesson(lessonNumber) {
  const index = PERIOD_LESSON_RANGES.findIndex(([from, to]) => lessonNumber >= from && lessonNumber <= to);
  return index === -1 ? 10 : index + 1;
}

/**
 * Cieľová rýchlosť pre dané obdobie — najnižšia rýchlosť na jednotku.
 * @param {number} lessonNumber
 * @param {number} [targetSpeed]
 */
export function targetSpeedForLesson(lessonNumber, targetSpeed = DEFAULT_TARGET_SPEED) {
  const period = periodForLesson(lessonNumber);
  return speedThreshold(period, 1, targetSpeed);
}

/**
 * Hranica rýchlosti pre daný stupeň a obdobie.
 * @param {number} period 1–10
 * @param {number} grade 1–4
 * @param {number} [targetSpeed]
 */
export function speedThreshold(period, grade, targetSpeed = DEFAULT_TARGET_SPEED) {
  const row = SPEED_TABLE_FOR_140[grade];
  if (!row) return 0;
  const base = row[Math.min(9, Math.max(0, period - 1))];
  // Pri inom cieli sa tabuľka úmerne prepočíta (predloha si ju generuje z cieľa).
  return Math.round((base * targetSpeed) / DEFAULT_TARGET_SPEED);
}

/**
 * Hranica chybovosti pre daný stupeň.
 * @param {number} grade 1–4
 * @param {number} [targetErrorRate]
 */
export function errorRateThreshold(grade, targetErrorRate = DEFAULT_TARGET_ERROR_RATE) {
  const multiplier = ERROR_RATE_MULTIPLIERS[grade - 1];
  if (multiplier === undefined) return Number.POSITIVE_INFINITY;
  return Math.round(targetErrorRate * multiplier * 100) / 100;
}

/**
 * Stupeň za rýchlosť.
 * @param {number} netCpm čisté údery za minútu
 * @param {number} [targetSpeed]
 * @param {number} [period] obdobie výučby 1–10
 */
export function gradeForSpeed(netCpm, targetSpeed = DEFAULT_TARGET_SPEED, period = 10) {
  for (const grade of [1, 2, 3, 4]) {
    if (netCpm >= speedThreshold(period, grade, targetSpeed)) return grade;
  }
  return 5;
}

/**
 * Stupeň za chybovosť.
 * @param {number} errorRatePercent percento chýb
 * @param {number} [targetErrorRate]
 */
export function gradeForErrorRate(errorRatePercent, targetErrorRate = DEFAULT_TARGET_ERROR_RATE) {
  for (const grade of [1, 2, 3, 4]) {
    if (errorRatePercent <= errorRateThreshold(grade, targetErrorRate)) return grade;
  }
  return 5;
}

/**
 * Výsledná známka: horší z dvoch stupňov (oficiálne pravidlo).
 * @param {number} speedGrade
 * @param {number} errorGrade
 */
export function finalGrade(speedGrade, errorGrade) {
  return Math.max(speedGrade, errorGrade);
}

/** @param {number} grade */
export function gradeLabel(grade) {
  return GRADES.find((item) => item.grade === grade)?.label ?? '—';
}

/**
 * Kompletná klasifikácia jedného pokusu.
 * @param {{netCpm: number, errorRatePercent: number, targetSpeed?: number, targetErrorRate?: number, lessonNumber?: number, period?: number}} input
 */
export function classify(input) {
  const targetSpeed = input.targetSpeed ?? DEFAULT_TARGET_SPEED;
  const targetErrorRate = input.targetErrorRate ?? DEFAULT_TARGET_ERROR_RATE;
  const period = input.period ?? (input.lessonNumber ? periodForLesson(input.lessonNumber) : 10);
  const speedGrade = gradeForSpeed(input.netCpm, targetSpeed, period);
  const errorGrade = gradeForErrorRate(input.errorRatePercent, targetErrorRate);
  const grade = finalGrade(speedGrade, errorGrade);
  return {
    speedGrade,
    errorGrade,
    grade,
    label: gradeLabel(grade),
    period,
    targetSpeed,
    targetErrorRate,
    speedNeeded: speedThreshold(period, grade, targetSpeed),
  };
}
